import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useT } from '../i18n';

/*
  InternalLinkFooter — drop-in section that links every page back to the four
  conversion-critical surfaces (Free Audit, Pricing, How It Works, AI Solutions)
  plus a related-services row. Used on Home, Pricing, Contact and How It Works.
*/

const LINKS_EN = [
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'AI Solutions', href: '/index-ai' },
  { label: 'Restaurant Technology', href: '/it-pos-systems' },
  { label: 'Digital Presence', href: '/digital-branding' },
  { label: 'Restaurant Growth', href: '/restaurant-revenue-growth' },
  { label: 'Data & Analytics', href: '/services/data-analytics' },
  { label: 'Free Audit', href: '/#audit' },
];

const LINKS_ES = [
  { label: 'Cómo Funciona', href: '/how-it-works' },
  { label: 'Precios', href: '/pricing' },
  { label: 'Soluciones IA', href: '/index-ai' },
  { label: 'Tecnología para Restaurantes', href: '/it-pos-systems' },
  { label: 'Presencia Digital', href: '/digital-branding' },
  { label: 'Crecimiento del Restaurante', href: '/restaurant-revenue-growth' },
  { label: 'Datos y Analítica', href: '/services/data-analytics' },
  { label: 'Auditoría Gratis', href: '/#audit' },
];

export default function InternalLinkFooter() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const links = isEs ? LINKS_ES : LINKS_EN;

  return (
    <section className="py-16 lg:py-20 bg-white border-t border-neutral-100" data-testid="internal-link-footer">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">
              {isEs ? 'Explora más' : 'Explore more'}
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-neutral-900">
              {isEs ? 'Una plataforma. Cada restaurante.' : 'One platform. Every restaurant.'}
            </h2>
          </div>
          <Link
            to="/contact#contact-form"
            className="inline-flex items-center gap-1 text-[#F77F11] hover:text-[#d8651a] font-semibold text-sm"
            data-testid="internal-link-talk-expert"
          >
            {isEs ? 'Habla con un experto' : 'Talk to an expert'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {links.map((l, i) => (
            <motion.div
              key={l.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.35, delay: i * 0.03 }}
            >
              <Link
                to={l.href}
                className="group flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3 hover:border-[#F77F11] hover:bg-orange-50/40 transition-all"
                data-testid={`internal-link-${l.href.replace(/[^a-z0-9]+/gi, '-').replace(/(^-|-$)/g, '')}`}
              >
                <span className="font-semibold text-neutral-800 text-[14px] group-hover:text-[#F77F11] transition-colors">{l.label}</span>
                <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-[#F77F11] group-hover:translate-x-1 transition-all" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
